import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthState, TenantConfig, TenantInfo, ConfigResponse } from '../types';
import { apiClient } from '../lib/api';
import { cacheService } from '../lib/cache';
import { isRestaurantOpen } from '../lib/date';

interface AuthStore extends AuthState {
  config: TenantConfig | null;
  tenant: TenantInfo | null;
  setSessionToken: (sessionToken: string) => void;
  loginWithGoogle: (idToken: string) => Promise<void>;
  loginNative: (email: string, password: string) => Promise<void>;
  loginGuest: (email: string) => Promise<{
    token: string;
    client: {
      uuid: string;
      email: string;
      first_name: string;
      last_name: string;
      full_name: string;
      avatar_url: string | null;
    };
    message: string;
  }>;
  registerNative: (data: { email: string; password: string; first_name: string; last_name: string }) => Promise<void>;
  verifyClient: (email: string, code: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  setIdentifier: (identifier: string) => void;
  setTableId: (tableId: string | null) => void;
  setSessionId: (sessionId: string | null) => void;
  validateSession: (identifier: string, qrToken?: string) => Promise<boolean>;
  fetchConfig: () => Promise<void>;
  logout: () => void;
  setError: (error: string | null) => void;
  setValidating: (isValidating: boolean) => void;
  setTakeAway: (isTakeAway: boolean | null) => void;
  setTenantUuid: (tenantUuid: string | null) => void;
  regenerateAnonymousCartId: () => void;
  shouldPollOrders: boolean;
  setShouldPollOrders: (value: boolean) => void;
  orderTiming: 'today' | 'week' | null;
  setOrderTiming: (value: 'today' | 'week' | null) => void;
  orderMode: 'delivery' | 'pickup' | null;
  setOrderMode: (value: 'delivery' | 'pickup' | null) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isSessionClosed: boolean;
  setSessionClosed: (isClosed: boolean) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      client: null,
      sessionToken: null,
      clientToken: null,
      isValidating: false,
      error: null,
      tableId: null,
      sessionId: null,
      identifier: null,
      isTakeAway: null,
      tenantUuid: null,
      config: null,
      tenant: null,
      shouldPollOrders: false,
      orderTiming: null,
      orderMode: null,
      isOpen: true,
      isSessionClosed: false,

      setSessionClosed: (isClosed: boolean) => {
        set({ isSessionClosed: isClosed });
      },

      setShouldPollOrders: (value: boolean) => {
        set({ shouldPollOrders: value });
      },

      setOrderTiming: (value: 'today' | 'week' | null) => {
        set({ orderTiming: value });
      },

      setOrderMode: (value: 'delivery' | 'pickup' | null) => {
        set({ orderMode: value });
      },

      setSessionToken: (sessionToken: string) => {
        apiClient.setSessionToken(sessionToken);
        set({ sessionToken, isAuthenticated: true });
      },

      loginWithGoogle: async (idToken: string) => {
        set({ isValidating: true, error: null });
        try {
          const response = await apiClient.loginWithGoogle(idToken);
          const { token: clientTokenResponse, client } = response;

          // Store Client Token
          apiClient.setClientToken(clientTokenResponse);

          set({
            clientToken: clientTokenResponse,
            client,
            isAuthenticated: true, // User is authenticated with client token
            isValidating: false
          });

          // Link Session if sessionId exists
          const currentSessionId = get().sessionId;
          if (currentSessionId) {
            try {
              await apiClient.linkSession(currentSessionId);
            } catch (linkError) {
              console.error("Failed to link session:", linkError);
              // We don't fail the login if linking fails, but we might want to notify/log
            }
          }

        } catch (error: any) {
          console.error("Login failed", error);
          set({
            error: error.message || 'Error al iniciar sesión con Google',
            isValidating: false
          });
          throw error;
        }
      },

      loginNative: async (email: string, password: string) => {
        set({ isValidating: true, error: null });
        try {
          const response = await apiClient.loginNative(email, password);
          const { token: clientTokenResponse, client } = response;

          apiClient.setClientToken(clientTokenResponse);

          set({
            clientToken: clientTokenResponse,
            client,
            isAuthenticated: true,
            isValidating: false
          });

          // Link Session if sessionId exists
          const currentSessionId = get().sessionId;
          if (currentSessionId) {
            try {
              await apiClient.linkSession(currentSessionId);
            } catch (linkError) {
              console.error("Failed to link session:", linkError);
            }
          }
        } catch (error: any) {
          console.error("Native login failed", error);
          set({
            error: error.message || 'Credenciales inválidas',
            isValidating: false
          });
          throw error;
        }
      },

      loginGuest: async (email: string) => {
        set({ isValidating: true, error: null });
        try {
          const response = await apiClient.loginGuest(email);

          apiClient.setClientToken(response.token);

          set({
            clientToken: response.token,
            client: response.client,
            identifier: email,
            isAuthenticated: true,
            isValidating: false,
            error: null,
          });

          return response;
        } catch (error: any) {
          console.error("Guest login failed:", error);
          set({
            error: error.message || 'Error al identificarte como invitado',
            isValidating: false,
          });
          throw error;
        }
      },

      registerNative: async (data) => {
        set({ isValidating: true, error: null });
        try {
          const response = await apiClient.registerNative(data);
          const { token: clientTokenResponse, client } = response;

          apiClient.setClientToken(clientTokenResponse);

          set({
            clientToken: clientTokenResponse,
            client,
            isAuthenticated: true,
            isValidating: false
          });

          // Link Session if sessionId exists
          const currentSessionId = get().sessionId;
          if (currentSessionId) {
            try {
              await apiClient.linkSession(currentSessionId);
            } catch (linkError) {
              console.error("Failed to link session:", linkError);
            }
          }
        } catch (error: any) {
          console.error("Registration failed", error);

          let errorMessage = error.message || 'Error al registrar usuario';
          if (errorMessage.includes('Cliente with this email already exists')) {
            errorMessage = 'Ya existe una cuenta con este correo. Por favor, inicia sesión.';
          }

          set({
            error: errorMessage,
            isValidating: false
          });
          throw error;
        }
      },

      verifyClient: async (email: string, code: string) => {
        set({ isValidating: true, error: null });
        try {
          const response = await apiClient.verifyClient(email, code);
          const { token: clientTokenResponse, client } = response;

          if (clientTokenResponse) {
            apiClient.setClientToken(clientTokenResponse);
          }

          set((state) => ({
            clientToken: clientTokenResponse ?? state.clientToken,
            client: client ?? (state.client ? { ...state.client, is_verified: true } : null),
            isAuthenticated: true,
            isValidating: false
          }));

        } catch (error: any) {
          console.error("Verification failed", error);
          set({
            error: error.message || 'Código inválido',
            isValidating: false
          });
          throw error;
        }
      },

      requestPasswordReset: async (email: string) => {
        set({ isValidating: true, error: null });
        try {
          await apiClient.requestPasswordReset(email);
          set({ isValidating: false });
        } catch (error: any) {
          set({ error: error.message || 'Error al solicitar recuperación', isValidating: false });
          throw error;
        }
      },

      confirmPasswordReset: async (email, code, newPassword) => {
        set({ isValidating: true, error: null });
        try {
          await apiClient.confirmPasswordReset({ email, code, new_password: newPassword });
          set({ isValidating: false });
        } catch (error: any) {
          set({ error: error.message || 'Error al restablecer contraseña', isValidating: false });
          throw error;
        }
      },


      setIdentifier: (identifier: string) => {
        const currentIdentifier = get().identifier;
        if (currentIdentifier && currentIdentifier !== identifier) {
          // Tenant cambió → limpiar datos de sesión del tenant anterior
          apiClient.setSessionToken(null);
          cacheService.clear();
          set({
            sessionToken: null,
            tableId: null,
            sessionId: null,
            isTakeAway: null,
            tenantUuid: null,
            config: null,
            tenant: null,
            orderTiming: null,
            orderMode: null,
            isSessionClosed: false,
            shouldPollOrders: false,
          });
          // Limpiar carrito del tenant anterior
          // Import dinámico para evitar dependencia circular
          import('./useCartStore').then(({ useCartStore }) => {
            useCartStore.getState().clearAll();
          });
        }
        apiClient.setIdentifier(identifier);
        set({ identifier });
      },

      setTableId: (tableId: string | null) => {
        set({ tableId });
      },

      setSessionId: (sessionId: string | null) => {
        set({ sessionId });
      },

      setTakeAway: (isTakeAway: boolean | null) => {
        set({ isTakeAway });
      },

      setTenantUuid: (tenantUuid: string | null) => {
        set({ tenantUuid });
      },

      regenerateAnonymousCartId: () => {
        const newId = crypto.randomUUID();
        sessionStorage.setItem('anonymous_cart_id', newId);
      },

      fetchConfig: async () => {
        try {
          const response = await apiClient.getConfig() as ConfigResponse;
          if (response?.success && response?.config) {
            const config = response.config;
            const tenant = response.tenant;
            set({
              config,
              tenant,
              isOpen: isRestaurantOpen(config)
            });
          }
        } catch (error) {
          console.error('Failed to fetch config:', error);
        }
      },

      setIsOpen: (isOpen: boolean) => {
        set({ isOpen });
      },

      validateSession: async (identifier: string, qrToken?: string) => {
        set({ isValidating: true, error: null });
        try {
          // Setear el identifier en el client antes de validar
          apiClient.setIdentifier(identifier);

          // Si se pasa un qrToken, preferirlo y evitar usar un token viejo en memoria
          const tokenToValidate = qrToken ?? (get().sessionToken || undefined);

          if (qrToken) {
            apiClient.setSessionToken(null);
          }

          const response = await apiClient.validateSession(tokenToValidate);

          if (response && response.valid && response.session_token) {
            apiClient.setSessionToken(response.session_token);

            // Intentar obtener table_id del response, o decodificarlo del token si no viene
            let finalTableId = response.table_id;

            if (!finalTableId && response.session_token) {
              try {
                // Decodificar JWT manualmente para evitar dependencias extra si es posible
                const base64Url = response.session_token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
                  return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                }).join(''));

                const payload = JSON.parse(jsonPayload);
                if (payload.table_uuid) {
                  finalTableId = payload.table_uuid;
                }
              } catch (e) {
                console.error("Error decoding JWT for table_uuid:", e);
              }
            }

            set({
              sessionToken: response.session_token,
              identifier,
              isAuthenticated: true,
              isValidating: false,
              error: null,
              tableId: finalTableId ?? null,
              sessionId: response.session_id ?? null,
              isTakeAway: response.take_away,
              tenantUuid: response.tenant_uuid ?? null,
            });

            // Fetch config after successful validation
            await get().fetchConfig();

            return true;
          } else {
            apiClient.setSessionToken(null);
            set((state) => ({
              sessionToken: null,
              isAuthenticated: false,
              isValidating: false,
              error: 'Apareció un error inesperado, por favor vuelve a iniciar sesión.',
              tableId: null,
              sessionId: null,
              // Preserve context flags if they exist
              isTakeAway: state.isTakeAway,
              tenantUuid: state.tenantUuid,
              identifier: state.identifier,
            }));
            return false;
          }
        } catch (error) {
          console.error('Session validation error:', error);
          apiClient.setSessionToken(null);
          set((state) => ({
            sessionToken: null,
            isAuthenticated: false,
            isValidating: false,
            error: 'Error de conexión con el servidor',
            tableId: null,
            sessionId: null,
            // Preserve context flags if they exist
            isTakeAway: state.isTakeAway,
            tenantUuid: state.tenantUuid,
            identifier: state.identifier,
          }));
          return false;
        }
      },

      logout: () => {
        apiClient.setSessionToken(null);
        apiClient.setClientToken(null);
        cacheService.clear(); // Limpiar caché al hacer logout
        set({
          isAuthenticated: false,
          sessionToken: null,
          clientToken: null,
          client: null,
          identifier: null,
          tableId: null,
          sessionId: null,
          error: null,
          isTakeAway: null,
          tenantUuid: null,
          config: null,
          tenant: null,
        });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      setValidating: (isValidating: boolean) => {
        set({ isValidating });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sessionToken: state.sessionToken,
        clientToken: state.clientToken,
        identifier: state.identifier,
        tableId: state.tableId,
        sessionId: state.sessionId,
        isAuthenticated: state.isAuthenticated,
        isTakeAway: state.isTakeAway,
        tenantUuid: state.tenantUuid,
        config: state.config,
        tenant: state.tenant,
        client: state.client,
        orderTiming: state.orderTiming,
        orderMode: state.orderMode,
        // we explicitly NOT include shouldPollOrders here
      })
    }
  )
);